import { motion } from "framer-motion";
import { File, FilePdf, FileText, FileImage, FileVideo, FileAudio, Sparkle, Trash, UploadSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

function getFileIcon(fileExt) {
  switch (fileExt) {
    case "pdf":
      return <FilePdf size={32} weight="duotone" className="text-red-500" />;
    case "txt":
    case "md":
      return <FileText size={32} weight="duotone" className="text-blue-500" />;
    case "png":
    case "jpg":
    case "jpeg":
    case "webp":
      return <FileImage size={32} weight="duotone" className="text-green-500" />;
    case "mp4":
    case "mov":
    case "avi":
      return <FileVideo size={32} weight="duotone" className="text-purple-500" />;
    case "mp3":
    case "wav":
      return <FileAudio size={32} weight="duotone" className="text-yellow-500" />;
    default:
      return <File size={32} weight="duotone" className="text-gray-500" />;
  }
}

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

export const FileList = ({ files, selectedFile, onSelectFile }) => (
  <div className="w-1/3 border-r border-border overflow-y-auto p-4" data-testid="files-list">
    <h3 className="text-sm font-semibold mb-3 uppercase tracking-wider text-muted-foreground">
      Your Files ({files.length})
    </h3>
    {files.length === 0 ? (
      <div className="text-center py-12">
        <UploadSimple size={48} className="mx-auto mb-3 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">No files uploaded yet</p>
      </div>
    ) : (
      <div className="space-y-2">
        {files.map((file) => (
          <motion.div
            key={file.id}
            onClick={() => onSelectFile(file)}
            className={`p-3 border cursor-pointer transition-all ${
              selectedFile?.id === file.id ? "border-white bg-secondary" : "border-border hover:border-white/40"
            }`}
            data-testid={`file-item-${file.id}`}
          >
            <div className="flex items-start gap-3">
              {getFileIcon(file.file_ext)}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{file.filename}</p>
                <p className="text-xs text-muted-foreground">
                  {formatFileSize(file.size)} &bull; {new Date(file.uploaded_at).toLocaleDateString()}
                </p>
                {file.ai_analysis && (
                  <div className="flex items-center gap-1 mt-1">
                    <Sparkle size={12} className="text-purple-500" />
                    <span className="text-xs text-purple-500">AI Analyzed</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    )}
  </div>
);

export const FileDetails = ({ selectedFile, onDelete }) => {
  if (!selectedFile) {
    return (
      <div className="flex items-center justify-center h-full text-center">
        <div>
          <File size={64} className="mx-auto mb-4 text-muted-foreground opacity-30" />
          <p className="text-muted-foreground">Select a file to view details and AI analysis</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-4">
          {getFileIcon(selectedFile.file_ext)}
          <div>
            <h3 className="text-xl font-bold mb-1">{selectedFile.filename}</h3>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>Type: {selectedFile.file_type}</p>
              <p>Size: {formatFileSize(selectedFile.size)}</p>
              <p>Uploaded: {new Date(selectedFile.uploaded_at).toLocaleString()}</p>
            </div>
          </div>
        </div>
        <Button
          onClick={() => onDelete(selectedFile.id)}
          variant="ghost"
          size="sm"
          className="hover:bg-destructive hover:text-white"
          data-testid="delete-file-button"
        >
          <Trash size={18} />
        </Button>
      </div>

      {selectedFile.ai_analysis && (
        <div className="border border-border p-6 bg-surface/50">
          <div className="flex items-center gap-2 mb-4">
            <Sparkle size={24} weight="fill" className="text-purple-500" />
            <h4 className="text-lg font-semibold">AI Analysis</h4>
          </div>
          <div className="prose prose-invert max-w-none">
            <pre className="whitespace-pre-wrap text-sm leading-relaxed font-sans">
              {selectedFile.ai_analysis}
            </pre>
          </div>
        </div>
      )}

      <div className="mt-6 p-4 border border-border bg-surface/30">
        <h4 className="text-sm font-semibold mb-3">Quick Actions</h4>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" className="border-border" size="sm" data-testid="action-create-task">
            Create Task from File
          </Button>
          <Button variant="outline" className="border-border" size="sm" data-testid="action-add-book">
            Add to Book
          </Button>
          <Button variant="outline" className="border-border" size="sm" data-testid="action-discuss">
            Discuss with AI
          </Button>
          <Button variant="outline" className="border-border" size="sm" data-testid="action-deep-analysis">
            Deep Analysis
          </Button>
        </div>
      </div>
    </div>
  );
};
